import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { applyTheme, prefersDark, readThemeMode, watchSystemTheme, writeThemeMode, type EffectiveTheme, type ThemeMode } from './theme'

type ThemeContextValue = {
    /** Co si uživatel zvolil (včetně možnosti řídit se systémem). */
    mode: ThemeMode
    /** Motiv, který je díky té volbě skutečně vidět. */
    theme: EffectiveTheme
    setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [mode, setModeState] = useState<ThemeMode>(readThemeMode)
    const [systemDark, setSystemDark] = useState<boolean>(prefersDark)

    // Při volbě "podle systému" musí aplikace reagovat i na přepnutí za běhu.
    useEffect(() => watchSystemTheme(setSystemDark), [])

    const theme: EffectiveTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode

    useEffect(() => {
        applyTheme(theme)
    }, [theme])

    const setMode = useCallback((next: ThemeMode) => {
        writeThemeMode(next)
        setModeState(next)
    }, [])

    const value = useMemo<ThemeContextValue>(() => ({ mode, theme, setMode }), [mode, theme, setMode])

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme lze použít jen uvnitř ThemeProvider')
    }
    return context
}
